// Client-portal project view — project-based layout (rework).
// Rendered for portal roles (and internal users previewing the portal). Reuses existing
// project data, the /compliance portal_view, the built PortalComplianceHub, the shared
// IncidentDialog (create/edit/add-action) and the SiteQRPoster. Frontend-only.
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    ArrowLeft, LayoutDashboard, CheckSquare, ListChecks, FileText, AlertTriangle,
    CalendarDays, Layers, ArrowUpRight, Clock, CircleCheck,
    Download, Inbox, HardHat, ClipboardCheck, ClipboardList, Users,
    Siren, Plus, X, Activity, ClipboardEdit, LogIn, LogOut, RefreshCw, Pencil, Radar, MapPin, Loader2, Wrench,
    ShieldCheck, Filter,
} from "lucide-react";
import Pill from "@/components/Pill";
import { toast } from "sonner";
import DocumentBrowser from "@/components/documents/DocumentBrowser";
import UnifiedCalendar from "@/components/calendar/UnifiedCalendar";
import PortalComplianceHub from "@/components/PortalComplianceHub";
import SiteQRPoster from "@/components/SiteQRPoster";
import ProjectOnSitePanel from "@/components/onsite/ProjectOnSitePanel";
import EquipmentOnSitePanel from "@/components/equipment/EquipmentOnSitePanel";
import AssemblyPointsEditor from "@/components/onsite/AssemblyPointsEditor";
import ProjectInductionRecords from "@/components/ProjectInductionRecords";
import { IncidentDialog } from "@/pages/safety/IncidentsPage";
import { useAuth } from "@/context/AuthContext";
import { api, filenameFromResponse } from "@/lib/api";
import { ProjectDialog } from "@/pages/ProjectsPage";

const CLIENT_SHARED = new Set(["client", "shared"]);
const RP_LABEL = { pr: "Priority Risk", client: "Your team", shared: "Shared" };
const PR_STAFF_ROLES = new Set(["director", "administrator", "consultant"]);

// Quick-action form buttons. `launch: true` opens the form directly for in-browser
// completion (site manager fills it in); otherwise opens the SiteQRPoster (self-service
// sign-on where many workers sign in on their own phones).
const QUICK_FORMS = [
    { code: "SITE-INDUCTION", label: "Site induction", Icon: HardHat, induction: true },
    { code: "WEEKLY-HS", label: "Weekly H&S inspection", Icon: ClipboardCheck, launch: true },
    { code: "QA-INSP", label: "QA inspection", Icon: ClipboardList, launch: true },
    { code: "TOOLBOX-PPE", label: "Toolbox talk", Icon: Users, toolbox: true },
];

function taskDone(ci) { return ci.status ? ci.status === "Complete" : !!ci.done; }
function isNA(ci) { return (ci.status || "") === "Not Applicable"; }
function todayIso() { return new Date().toISOString().slice(0, 10); }
function fmtDate(s) {
    if (!s) return null;
    try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return s; }
}

// Contribution model: pr% + client% = overall% (shares of the whole project).
function splitProgress(stages) {
    let totalW = 0, prDone = 0, clientDone = 0;
    for (const s of stages || []) {
        for (const ci of (s.checklist || [])) {
            if (isNA(ci)) continue;
            const w = Number(ci.weight || 1);
            totalW += w;
            if (taskDone(ci)) {
                if ((ci.responsible_party || "pr") === "pr") prDone += w;
                else clientDone += w;
            }
        }
    }
    const pct = (x) => (totalW ? Math.round((x / totalW) * 100) : 0);
    return { overall: pct(prDone + clientDone), pr: pct(prDone), client: pct(clientDone), hasTasks: totalW > 0 };
}

function ProgressBar({ label, value, colour, testid }) {
    return (
        <div data-testid={testid}>
            <div className="flex items-center justify-between mb-1">
                <span className="text-[11.5px] font-semibold" style={{ color: "#3e4b63" }}>{label}</span>
                <span className="font-mono text-[12px] font-bold text-[#1F1F1F]">{value}%</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: "#EFEAE3" }}>
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${value}%`, background: colour }} />
            </div>
        </div>
    );
}

function Panel({ title, icon: Icon, count, children, testid, brand, action }) {
    return (
        <div data-testid={testid} className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                <Icon className="w-4 h-4" style={{ color: brand }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] flex-1">{title}</h3>
                {count != null && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{count}</span>}
                {action}
            </div>
            {children}
        </div>
    );
}

// Client Admin's restricted edit surface for their own project — Reference, End
// client, Notes, Target date, and which Sites/Buildings/Contacts are linked.
// Everything PR-governed (Status, Stage, RIBA/CDM, Value, Lead consultant,
// Required forms) is not editable here — see backend ProjectPortalUpdate.
function EditProjectDetailsDialog({ project, onClose, onSaved, brand }) {
    const [form, setForm] = useState({
        reference: project.reference || "", end_client: project.end_client || "",
        target_date: project.target_date || "", notes: project.notes || "",
        site_ids: project.site_ids || [], building_ids: project.building_ids || [],
        member_contact_ids: project.member_contact_ids || [],
    });
    const [options, setOptions] = useState({ sites: [], buildings: [], contacts: [] });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([api.get("/sites"), api.get("/buildings"), api.get("/contacts")])
            .then(([s, b, c]) => setOptions({ sites: s.data, buildings: b.data, contacts: c.data }))
            .catch(() => {});
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const toggleIn = (k, id) => setForm((f) => ({
        ...f, [k]: f[k].includes(id) ? f[k].filter((x) => x !== id) : [...f[k], id],
    }));

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/projects/${project.id}/portal-details`, form);
            toast.success("Project details updated.");
            onSaved();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save changes.");
        } finally { setSaving(false); }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: "rgba(15,15,15,0.45)" }} data-testid="edit-project-details-dialog">
            <div className="bg-white rounded-[14px] shadow-brand w-full max-w-[560px] max-h-[85vh] overflow-y-auto">
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="text-[15px] font-bold text-[#1F1F1F]">Edit project details</h3>
                    <button data-testid="edit-project-details-close" onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <label className="text-[12px] font-semibold text-[#3e4b63]">Reference
                            <input data-testid="edit-project-reference" value={form.reference} onChange={(e) => set("reference", e.target.value)}
                                className="mt-1 w-full border rounded-[8px] px-3 py-2 text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                        <label className="text-[12px] font-semibold text-[#3e4b63]">Target date
                            <input type="date" data-testid="edit-project-target-date" value={form.target_date} onChange={(e) => set("target_date", e.target.value)}
                                className="mt-1 w-full border rounded-[8px] px-3 py-2 text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                        </label>
                    </div>
                    <label className="text-[12px] font-semibold text-[#3e4b63] block">End client
                        <input data-testid="edit-project-end-client" value={form.end_client} onChange={(e) => set("end_client", e.target.value)}
                            className="mt-1 w-full border rounded-[8px] px-3 py-2 text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                    <label className="text-[12px] font-semibold text-[#3e4b63] block">Scope / notes
                        <textarea data-testid="edit-project-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3}
                            className="mt-1 w-full border rounded-[8px] px-3 py-2 text-[13px]" style={{ borderColor: "#E5E0DA" }} />
                    </label>
                    {[
                        { key: "site_ids", label: "Sites", opts: options.sites },
                        { key: "building_ids", label: "Buildings", opts: options.buildings },
                        { key: "member_contact_ids", label: "Key contacts", opts: options.contacts },
                    ].map((grp) => (
                        <div key={grp.key}>
                            <div className="text-[12px] font-semibold text-[#3e4b63] mb-1.5">{grp.label}</div>
                            <div className="max-h-[120px] overflow-y-auto border rounded-[8px] p-2 space-y-1" style={{ borderColor: "#E5E0DA" }}>
                                {grp.opts.length === 0 && <div className="text-[11.5px] text-[#9A968F] px-1 py-1">None available</div>}
                                {grp.opts.map((o) => (
                                    <label key={o.id} data-testid={`edit-project-${grp.key}-${o.id}`} className="flex items-center gap-2 text-[12.5px] px-1 py-0.5 cursor-pointer">
                                        <input type="checkbox" checked={form[grp.key].includes(o.id)} onChange={() => toggleIn(grp.key, o.id)} />
                                        {o.name}
                                    </label>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="px-5 py-4 border-t flex justify-end gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="edit-project-details-cancel" onClick={onClose} className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button data-testid="edit-project-details-save" onClick={save} disabled={saving}
                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-60" style={{ background: brand }}>
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function OverviewRow({ label, value }) {
    if (value == null || value === "" || (Array.isArray(value) && value.length === 0)) return null;
    return (
        <div>
            <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#9A968F" }}>{label}</div>
            <div className="text-[13px] text-[#1F1F1F] mt-0.5">{value}</div>
        </div>
    );
}

function QAButton({ icon: Icon, label, onClick, brand, solid, testid }) {
    return (
        <button data-testid={testid} onClick={onClick}
            className="inline-flex items-center gap-2 rounded-[10px] px-3.5 py-2 text-[12.5px] font-semibold border transition-colors"
            style={solid
                ? { background: brand, color: "#fff", borderColor: brand }
                : { background: "#fff", color: "#1F1F1F", borderColor: "#E5E0DA" }}>
            <Icon className="w-4 h-4" style={{ color: solid ? "#fff" : brand }} /> {label}
        </button>
    );
}

const PORTAL_TABS = [
    { k: "home", l: "Home", Icon: LayoutDashboard },
    { k: "onsite", l: "On Site", Icon: Radar },
    { k: "equipment", l: "Equipment on Site", Icon: Wrench, flag: "equipment_register_enabled" },
    { k: "records", l: "Induction Records", Icon: ShieldCheck },
    { k: "mytasks", l: "My tasks", Icon: ListChecks },
    { k: "actions", l: "Actions", Icon: CheckSquare },
    { k: "reports", l: "Reports & documents", Icon: FileText },
    { k: "compliance", l: "Compliance Hub", Icon: Activity },
    { k: "incidents", l: "Incidents", Icon: AlertTriangle },
    { k: "calendar", l: "Calendar", Icon: CalendarDays },
];

export default function PortalProjectView({ project, compliance, related, client, onReload }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTabState] = useState(searchParams.get("tab") || "home");
    const setTab = (t) => {
        setTabState(t);
        const next = new URLSearchParams(searchParams);
        if (t === "home") next.delete("tab"); else next.set("tab", t);
        setSearchParams(next, { replace: true });
    };
    const [incidentDlg, setIncidentDlg] = useState(null);   // {incident} | null
    const [qrModal, setQrModal] = useState(null);           // {code,label} | null
    const [attModal, setAttModal] = useState(false);        // sign-in/out QR + on-site log
    const [inductionQr, setInductionQr] = useState(false);  // issue a specific induction QR
    const [toolboxQr, setToolboxQr] = useState(false);      // issue a Toolbox Talk register QR (talk picker)
    const [editDetailsOpen, setEditDetailsOpen] = useState(false);
    const [editProjectOpen, setEditProjectOpen] = useState(false);
    const [meta, setMeta] = useState({ types: [], severities: [], statuses: [] });
    const [buildings, setBuildings] = useState([]);
    const brand = client?.branding?.primary_colour || "var(--brand-accent,#C75A1B)";
    const today = todayIso();

    const isReviewer = user?.role === "reviewer";
    const canAct = !isReviewer;   // reviewer = read-only; everyone else on the portal can act
    const isClientAdmin = user?.role === "client_admin";
    const isPrStaffPreview = user && PR_STAFF_ROLES.has(user.role);   // PR staff previewing this client's portal
    const siteId = project.site_id || (project.site_ids || [])[0] || null;
    const backParam = `&back=${encodeURIComponent("/app/projects/" + project.id)}`;

    useEffect(() => {
        api.get("/incidents/meta").then((r) => setMeta(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
    }, []);

    const pv = compliance?.portal_view || {};
    const scoped = compliance?.project_scoped || {};
    const actions = scoped.actions || [];
    const documents = scoped.documents || [];
    const reports = scoped.inspections || []; // completed report/audit records (form_instances) for this project — every status
    const incidents = pv.incidents || [];
    const visits = (scoped.site_visits || []).filter((v) => v.status === "Scheduled" && (v.scheduled_date || "") >= today);
    const sites = related?.sites || [];
    const projBuildings = related?.buildings || [];

    const prog = splitProgress(project.stages);

    // "Needs your attention" — open client/shared actions + tasks.
    const attention = [];
    for (const a of actions) {
        if (a.status === "Complete") continue;
        if (!CLIENT_SHARED.has(a.responsible_party || "pr")) continue;
        attention.push({ type: "action", id: a.id, title: a.title, due_date: a.due_date, overdue: !!(a.due_date && a.due_date < today), tag: "Action" });
    }
    for (const s of (project.stages || [])) {
        for (const ci of (s.checklist || [])) {
            if (isNA(ci) || taskDone(ci)) continue;
            if (!CLIENT_SHARED.has(ci.responsible_party || "pr")) continue;
            attention.push({ type: "task", id: ci.id, title: ci.label, due_date: ci.due_date, overdue: !!(ci.due_date && ci.due_date < today), tag: s.label || "Task" });
        }
    }
    attention.sort((a, b) => (Number(b.overdue) - Number(a.overdue)) || ((a.due_date || "9999").localeCompare(b.due_date || "9999")));

    const requestedDocs = documents.filter((d) => ["Requested", "Missing", "Under review"].includes(d.status));

    const matrix = [
        { label: "Tasks", src: (project.stages || []).flatMap((s) => (s.checklist || []).filter((c) => !isNA(c))) },
        { label: "Actions", src: actions },
        { label: "Reports", src: reports },
        { label: "Documents", src: documents },
    ].map((row) => {
        const c = { pr: 0, client: 0, shared: 0 };
        for (const it of row.src) { const rp = it.responsible_party || "pr"; c[rp] = (c[rp] || 0) + 1; }
        return { label: row.label, ...c };
    });

    const openAction = (id) => navigate(`/app/actions?open=${id}&back=${encodeURIComponent("/app/projects/" + project.id)}`);

    // Launch a form directly for in-browser completion (site manager fills it in).
    const launchForm = (code) => navigate(
        `/app/inspections?new=1&template=${code}&project=${project.id}&client=${project.client_id}` +
        `&project_label=${encodeURIComponent(project.name)}${backParam}`);

    async function openIncident(row) {
        try {
            const r = await api.get(`/incidents/${row.id}`);
            setIncidentDlg({ incident: r.data });
        } catch {
            setIncidentDlg({ incident: row });   // fall back to the list row
        }
    }
    const reloadCompliance = () => { if (onReload) onReload(); };

    const money = (project.value && !client?.hide_financials) ? `£${Number(project.value).toLocaleString()}` : null;

    return (
        <div data-testid="portal-project-view" className="space-y-5 pb-10">
            <button onClick={() => navigate("/app/projects")} data-testid="portal-project-back"
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: "#3e4b63" }}>
                <ArrowLeft className="w-3.5 h-3.5" /> Back to projects
            </button>

            {/* Project head + split progress */}
            <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-2xl font-bold tracking-tight text-[#1F1F1F]">{project.name}</h2>
                            {project.workflow_stage && <Pill tone="blue">{project.workflow_stage}</Pill>}
                        </div>
                        <div className="text-[11.5px] font-mono mt-1" style={{ color: "#64718a" }}>
                            {project.reference}{(project.project_types || []).length ? ` · ${project.project_types.join(", ")}` : (project.project_type ? ` · ${project.project_type}` : "")}
                        </div>
                    </div>
                </div>
                {prog.hasTasks && (
                    <div className="grid sm:grid-cols-3 gap-4 mt-4 pt-4 border-t" style={{ borderColor: "#F0EDE8" }}>
                        <ProgressBar testid="progress-overall" label="Overall" value={prog.overall} colour={brand} />
                        <ProgressBar testid="progress-pr" label="Priority Risk work" value={prog.pr} colour="#6F6A64" />
                        <ProgressBar testid="progress-client" label="Your actions" value={prog.client} colour="#2E7D4F" />
                    </div>
                )}
            </div>

            {/* Phase 1 — Quick Actions bar (above the tabs) */}
            {canAct && (
                <div data-testid="portal-quick-actions" className="bg-white border rounded-[13px] shadow-brand-sm p-3 flex flex-wrap items-center gap-2" style={{ borderColor: "#E5E0DA" }}>
                    <span className="text-[10.5px] font-bold uppercase tracking-wider mr-1" style={{ color: "#9A968F" }}>Quick actions</span>
                    <QAButton testid="qa-signin-qr" icon={LogIn} label="Sign-in QR" brand={brand} onClick={() => setAttModal(true)} />
                    {QUICK_FORMS.map((f) => (
                        <QAButton key={f.code} testid={`qa-form-${f.code}`} icon={f.Icon} label={f.label} brand={brand}
                            onClick={() => f.induction ? setInductionQr(true) : f.toolbox ? setToolboxQr(true) : (f.launch ? launchForm(f.code) : setQrModal(f))} />
                    ))}
                    <QAButton testid="qa-log-incident" icon={Siren} label="Log incident" solid brand={brand} onClick={() => setIncidentDlg({ incident: null })} />
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 flex-wrap border-b" style={{ borderColor: "#E5E0DA" }} data-testid="portal-project-tabs">
                {PORTAL_TABS.filter((t) => !t.flag || client?.[t.flag]).map((t) => (
                    <button key={t.k} data-testid={`portal-ptab-${t.k}`} onClick={() => setTab(t.k)}
                        className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[13px] font-semibold rounded-t-[8px] border-b-2 -mb-px ${tab === t.k ? "text-[#1F1F1F]" : "border-transparent text-[#64718a] hover:text-[#3e4b63]"}`}
                        style={tab === t.k ? { borderColor: brand } : undefined}>
                        <t.Icon className="w-4 h-4" style={{ color: tab === t.k ? brand : "#94a0b4" }} />
                        {t.l}
                    </button>
                ))}
            </div>

            {tab === "home" && (
                <div className="space-y-5" data-testid="portal-home-tab">
                    {/* Phase 2 — Overview (PR-entered detail; Client Admin can edit a restricted subset) */}
                    <Panel title="Project overview" icon={LayoutDashboard} testid="portal-project-overview" brand={brand}
                        action={(isPrStaffPreview || isClientAdmin) && (
                            <button data-testid={isPrStaffPreview ? "portal-edit-project-full" : "portal-edit-project-details"}
                                onClick={() => isPrStaffPreview ? setEditProjectOpen(true) : setEditDetailsOpen(true)}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: brand }}>
                                <Pencil className="w-3.5 h-3.5" /> Edit project
                            </button>
                        )}>
                        <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-4">
                            <OverviewRow label="Reference" value={project.reference} />
                            <OverviewRow label="Type" value={(project.project_types || []).length ? project.project_types.join(", ") : project.project_type} />
                            <OverviewRow label="Status" value={project.status} />
                            <OverviewRow label="Stage" value={project.workflow_stage} />
                            <OverviewRow label="RIBA stage" value={project.riba_stage} />
                            <OverviewRow label="CDM role" value={project.cdm_role} />
                            <OverviewRow label="F10 number" value={project.f10_number} />
                            <OverviewRow label="Start date" value={fmtDate(project.start_date)} />
                            <OverviewRow label="Target date" value={fmtDate(project.target_date)} />
                            <OverviewRow label="Value" value={money} />
                            <OverviewRow label="Lead consultant (PR)" value={project.lead_consultant} />
                            <OverviewRow label="End client" value={project.end_client} />
                            <OverviewRow label={`Sites (${sites.length})`} value={sites.length ? sites.map((s) => s.name).join(", ") : (project.site_ids || []).length || null} />
                            <OverviewRow label={`Buildings (${projBuildings.length})`} value={projBuildings.length ? projBuildings.map((b) => b.name).join(", ") : null} />
                            <OverviewRow label="Required forms / audits" value={(project.required_forms || []).join(", ")} />
                            <OverviewRow label="Key contacts" value={(project.member_contact_ids || []).length || null} />
                        </div>
                        {project.notes && (
                            <div className="px-5 pb-5">
                                <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#9A968F" }}>Scope / notes</div>
                                <p className="text-[13px] text-[#3e4b63] mt-1 whitespace-pre-line leading-relaxed">{project.notes}</p>
                            </div>
                        )}
                    </Panel>

                    <div className="grid lg:grid-cols-2 gap-5">
                        <Panel title="Needs your attention" icon={CheckSquare} count={attention.length} testid="project-attention-list" brand={brand}>
                            <ul>
                                {attention.slice(0, 8).map((t) => (
                                    <li key={`${t.type}-${t.id}`} data-testid={`attention-${t.id}`}
                                        onClick={() => t.type === "action" ? openAction(t.id) : setTab("mytasks")}
                                        className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{t.title}</div>
                                            <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{t.tag}</div>
                                        </div>
                                        {t.overdue ? <Pill tone="red"><Clock className="w-3 h-3 inline mr-0.5" />Overdue</Pill>
                                            : t.due_date ? <Pill tone="neutral">{fmtDate(t.due_date)}</Pill> : null}
                                        <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brand }} />
                                    </li>
                                ))}
                                {attention.length === 0 && (
                                    <li className="px-5 py-8 text-center">
                                        <CircleCheck className="w-7 h-7 mx-auto mb-2" style={{ color: "#5FA35A" }} />
                                        <div className="text-[12.5px] font-semibold text-[#1F1F1F]">You're all caught up</div>
                                    </li>
                                )}
                            </ul>
                        </Panel>

                        <Panel title="Reports issued by Priority Risk" icon={FileText} count={reports.length} testid="published-reports-panel" brand={brand}
                            action={reports.length > 0 && (
                                <button data-testid="reports-view-all" onClick={() => setTab("reports")}
                                    className="inline-flex items-center gap-1 text-[11.5px] font-semibold" style={{ color: brand }}>
                                    View all <ArrowUpRight className="w-3.5 h-3.5" />
                                </button>
                            )}>
                            <ReportRows reports={[...reports].sort((a, b) => (b.submitted_at || b.created_at || "").localeCompare(a.submitted_at || a.created_at || "")).slice(0, 6)}
                                projectId={project.id} navigate={navigate} />
                        </Panel>

                        <Panel title="Documents requested from you" icon={Inbox} count={requestedDocs.length} testid="requested-documents-panel" brand={brand}>
                            <ul>
                                {requestedDocs.map((d) => (
                                    <li key={d.id} data-testid={`requested-doc-${d.id}`} className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{d.title}</div>
                                            <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{d.category}</div>
                                        </div>
                                        <Pill tone={d.status === "Missing" ? "red" : "amber"}>{d.status === "Under review" ? "Under PR review" : d.status}</Pill>
                                    </li>
                                ))}
                                {requestedDocs.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">Nothing requested from you right now.</li>}
                            </ul>
                        </Panel>

                        <Panel title="Upcoming Priority Risk visits" icon={CalendarDays} count={visits.length} testid="upcoming-visits-panel" brand={brand}>
                            <ul>
                                {visits.slice(0, 6).map((v) => (
                                    <li key={v.id} className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{v.title || v.visit_type}</div>
                                            <div className="text-[10.5px] text-[#9A968F] mt-0.5">{v.assignee_name || "Priority Risk"}</div>
                                        </div>
                                        <Pill tone="blue">{fmtDate(v.scheduled_date)}</Pill>
                                    </li>
                                ))}
                                {visits.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No upcoming visits scheduled.</li>}
                            </ul>
                        </Panel>
                    </div>

                    <Panel title="Who owns what" icon={Layers} testid="responsibility-matrix" brand={brand}>
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="text-[10.5px] uppercase tracking-wide" style={{ color: "#9A968F" }}>
                                    <th className="text-left px-5 py-2 font-semibold">Item</th>
                                    <th className="text-center px-3 py-2 font-semibold">Priority Risk</th>
                                    <th className="text-center px-3 py-2 font-semibold">Your team</th>
                                    <th className="text-center px-3 py-2 font-semibold">Shared</th>
                                </tr>
                            </thead>
                            <tbody>
                                {matrix.map((r) => (
                                    <tr key={r.label} data-testid={`matrix-row-${r.label.toLowerCase()}`} className="border-t" style={{ borderColor: "#F0EDE8" }}>
                                        <td className="px-5 py-2.5 font-semibold text-[#1F1F1F]">{r.label}</td>
                                        <td className="text-center px-3 py-2.5 text-[#6F6A64]">{r.pr}</td>
                                        <td className="text-center px-3 py-2.5 font-semibold" style={{ color: r.client ? "#2E7D4F" : "#B8B2A8" }}>{r.client}</td>
                                        <td className="text-center px-3 py-2.5" style={{ color: r.shared ? "#C68A1C" : "#B8B2A8" }}>{r.shared}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Panel>
                </div>
            )}

            {tab === "onsite" && (
                <ProjectOnSitePanel clientId={project.client_id} projectId={project.id} siteId={siteId} />
            )}

            {tab === "equipment" && (
                <div className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }} data-testid="portal-equipment-tab">
                    <EquipmentOnSitePanel clientId={project.client_id} projectId={project.id}
                        canEdit={["client_admin", "site_manager"].includes(user?.role)} registerEnabled={!!client?.equipment_register_enabled} />
                </div>
            )}

            {tab === "records" && (
                <ProjectInductionRecords projectId={project.id} brand={brand} />
            )}

            {tab === "mytasks" && (
                <Panel title="My tasks" icon={ListChecks} count={attention.length} testid="portal-mytasks-tab" brand={brand}>
                    <ul>
                        {attention.map((t) => (
                            <li key={`${t.type}-${t.id}`} data-testid={`mytask-${t.id}`}
                                onClick={() => t.type === "action" ? openAction(t.id) : null}
                                className={`flex items-center gap-3 px-5 py-3 border-b last:border-b-0 ${t.type === "action" ? "hover:bg-[#FCFBF9] cursor-pointer" : ""}`} style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{t.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{t.type === "action" ? "Action" : `Task · ${t.tag}`}</div>
                                </div>
                                {t.overdue ? <Pill tone="red">Overdue</Pill> : t.due_date ? <Pill tone="neutral">{fmtDate(t.due_date)}</Pill> : null}
                            </li>
                        ))}
                        {attention.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No tasks assigned to your team.</li>}
                    </ul>
                </Panel>
            )}

            {tab === "actions" && (
                <Panel title="Actions" icon={CheckSquare} count={actions.length} testid="portal-actions-tab" brand={brand}>
                    <ul>
                        {actions.map((a) => (
                            <li key={a.id} data-testid={`portal-action-${a.id}`} onClick={() => openAction(a.id)}
                                className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{a.ref ? `${a.ref} — ` : ""}{a.title}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{RP_LABEL[a.responsible_party || "pr"]}{a.assignee_name ? ` · ${a.assignee_name}` : ""}</div>
                                </div>
                                <Pill tone={a.status === "Complete" ? "green" : (a.due_date && a.due_date < today ? "red" : "neutral")}>{a.status}</Pill>
                                <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brand }} />
                            </li>
                        ))}
                        {actions.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No actions on this project.</li>}
                    </ul>
                </Panel>
            )}

            {tab === "reports" && (
                <div data-testid="portal-reports-tab" className="space-y-5">
                    <ProjectReportsList reports={reports} projectId={project.id} navigate={navigate} brand={brand} />
                    <DocumentBrowser clientId={project.client_id} projectId={project.id} projectLabel={project.name}
                        user={user} clients={client ? [client] : []} />
                </div>
            )}

            {/* Phase 3 — Compliance Hub */}
            {tab === "compliance" && (
                <div data-testid="portal-compliance-tab">
                    <PortalComplianceHub compliance={compliance} project={project} backParam={backParam} />
                </div>
            )}

            {/* Phase 4 — Incidents: create + add actions in place */}
            {tab === "incidents" && (
                <Panel title="Incidents & near misses" icon={AlertTriangle} count={incidents.length} testid="portal-incidents-tab" brand={brand}
                    action={canAct && (
                        <button data-testid="portal-incidents-log" onClick={() => setIncidentDlg({ incident: null })}
                            className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: brand }}>
                            <Plus className="w-3.5 h-3.5" /> Log incident
                        </button>
                    )}>
                    <ul>
                        {incidents.map((i) => (
                            <li key={i.id} data-testid={`portal-incident-${i.id}`} onClick={() => openIncident(i)}
                                className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-[12.5px] font-semibold text-[#1F1F1F] truncate">{i.reference ? `${i.reference} — ` : ""}{i.incident_type}</div>
                                    <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{i.location || ""}{i.occurred_at ? ` · ${fmtDate(i.occurred_at)}` : ""}</div>
                                </div>
                                {i.riddor_reportable && <Pill tone="red">RIDDOR</Pill>}
                                <Pill tone={i.status === "Closed" ? "green" : "amber"}>{i.status}</Pill>
                                <ClipboardEdit className="w-3.5 h-3.5 flex-shrink-0" style={{ color: brand }} />
                            </li>
                        ))}
                        {incidents.length === 0 && <li className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No incidents logged on this project. Use “Log incident” to report one.</li>}
                    </ul>
                </Panel>
            )}

            {tab === "calendar" && (
                <div className="space-y-3" data-testid="portal-calendar-tab">
                    <UnifiedCalendar clientId={project.client_id} projectId={project.id} canEdit={false} role={user?.role} />
                </div>
            )}

            {/* Log incident / edit + add-action dialog (Phase 1 + Phase 4) */}
            {incidentDlg !== null && (
                <IncidentDialog
                    incident={incidentDlg.incident || null}
                    meta={meta}
                    clients={client ? [client] : []}
                    buildings={buildings}
                    portalClientId={project.client_id}
                    isReviewer={isReviewer}
                    viewerCanEdit={canAct}
                    canAskAi={user?.role !== "site_manager"}
                    prefill={{ client_id: project.client_id, project_id: project.id, site_id: siteId, building_id: (project.building_ids || [])[0] }}
                    onClose={() => setIncidentDlg(null)}
                    onSaved={() => { setIncidentDlg(null); reloadCompliance(); }}
                    onReturnToProject={() => { setIncidentDlg(null); reloadCompliance(); }}
                />
            )}

            {/* QR sign-on modal for a quick-action form (Phase 1) */}
            {qrModal && (
                <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="qa-qr-modal" onClick={() => setQrModal(null)}>
                    <div className="max-w-xl mx-auto mt-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-semibold text-[14px]">{qrModal.label} — QR sign-on</span>
                            <button data-testid="qa-qr-close" onClick={() => setQrModal(null)} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <SiteQRPoster clientId={project.client_id} siteId={siteId} projectId={project.id}
                            siteName={sites[0]?.name || project.name} projectName={project.name}
                            templateCode={qrModal.code} templateLabel={qrModal.label} />
                        <button data-testid="qa-qr-back" onClick={() => setQrModal(null)}
                            className="mt-3 w-full h-11 rounded-lg bg-white/95 text-[#1F1F1F] font-semibold text-[13px] inline-flex items-center justify-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Done — back to project
                        </button>
                    </div>
                </div>
            )}

            {/* Issue a specific induction — manager picks the template, QR is bound to it */}
            {inductionQr && (
                <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="induction-issue-modal" onClick={() => setInductionQr(false)}>
                    <div className="max-w-xl mx-auto mt-8 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <span className="text-white font-semibold text-[14px]">Issue a site induction</span>
                            <button data-testid="induction-issue-close" onClick={() => setInductionQr(false)} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <IssueInductionPanel clientId={project.client_id} siteId={siteId} projectId={project.id}
                            siteName={sites[0]?.name || project.name} projectName={project.name} brand={brand} />
                        <button data-testid="induction-issue-done" onClick={() => setInductionQr(false)}
                            className="w-full h-11 rounded-lg bg-white/95 text-[#1F1F1F] font-semibold text-[13px] inline-flex items-center justify-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Done — back to project
                        </button>
                    </div>
                </div>
            )}

            {/* Deliver a Toolbox Talk — manager picks the talk, print a QR register everyone signs (Phase 4) */}
            {toolboxQr && (
                <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="toolbox-issue-modal" onClick={() => setToolboxQr(false)}>
                    <div className="max-w-xl mx-auto mt-8 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <span className="text-white font-semibold text-[14px]">Deliver a Toolbox Talk</span>
                            <button data-testid="toolbox-issue-close" onClick={() => setToolboxQr(false)} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <SiteQRPoster clientId={project.client_id} siteId={siteId} projectId={project.id}
                            siteName={sites[0]?.name || project.name} projectName={project.name}
                            mode="register" templateCategory="toolbox" pickTemplate templateLabel="Toolbox Talk" />
                        <button data-testid="toolbox-issue-done" onClick={() => setToolboxQr(false)}
                            className="w-full h-11 rounded-lg bg-white/95 text-[#1F1F1F] font-semibold text-[13px] inline-flex items-center justify-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Done — back to project
                        </button>
                    </div>
                </div>
            )}

            {/* Sign-in / sign-out QR + on-site attendance log (Phase 1) */}
            {attModal && (
                <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto p-4" data-testid="attendance-modal" onClick={() => setAttModal(false)}>
                    <div className="max-w-xl mx-auto mt-8 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <span className="text-white font-semibold text-[14px]">Site sign-in / sign-out</span>
                            <button data-testid="attendance-close" onClick={() => setAttModal(false)} className="text-white/90 hover:text-white"><X className="w-5 h-5" /></button>
                        </div>
                        <SiteQRPoster clientId={project.client_id} siteId={siteId} projectId={project.id}
                            siteName={sites[0]?.name || project.name} projectName={project.name} mode="attendance" />
                        <AttendancePanel projectId={project.id} clientId={project.client_id} brand={brand} />
                        <button data-testid="attendance-done" onClick={() => setAttModal(false)}
                            className="w-full h-11 rounded-lg bg-white/95 text-[#1F1F1F] font-semibold text-[13px] inline-flex items-center justify-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> Done — back to project
                        </button>
                    </div>
                </div>
            )}

            {editDetailsOpen && (
                <EditProjectDetailsDialog project={project} brand={brand}
                    onClose={() => setEditDetailsOpen(false)} onSaved={reloadCompliance} />
            )}
            {editProjectOpen && (
                <ProjectDialog open={editProjectOpen} mode="edit" initial={project} clients={client ? [client] : []}
                    onClose={() => setEditProjectOpen(false)} onSaved={reloadCompliance} />
            )}
        </div>
    );
}

function IssueInductionPanel({ clientId, siteId, projectId, siteName, projectName, brand }) {
    const [templates, setTemplates] = useState([]);
    const [templateId, setTemplateId] = useState("");
    const [assemblyPoints, setAssemblyPoints] = useState([]);
    const [assemblyBusy, setAssemblyBusy] = useState(false);
    useEffect(() => {
        api.get(`/inductions/templates?client_id=${clientId}`).then((r) => {
            const active = (r.data || []).filter((t) => t.status === "published");
            setTemplates(active);
            const def = active.find((t) => t.is_default) || active[0];
            if (def) setTemplateId(def.id);
        }).catch(() => {});
    }, [clientId]);
    useEffect(() => {
        if (!siteId) return;
        api.get(`/inductions/sites?client_id=${clientId}`).then((r) => {
            const s = (r.data || []).find((x) => x.id === siteId);
            setAssemblyPoints(JSON.parse(JSON.stringify(s?.assembly_points || [])));
        }).catch(() => {});
    }, [clientId, siteId]);
    const tpl = templates.find((t) => t.id === templateId);

    async function saveAssembly() {
        setAssemblyBusy(true);
        try {
            await api.put(`/inductions/sites/${siteId}/assembly-points`, {
                blocks: assemblyPoints.map((p) => ({ id: p.id, heading: p.heading, body: p.body, image_path: p.image_path })),
            });
            toast.success("Assembly points saved");
        } catch { toast.error("Save failed"); }
        setAssemblyBusy(false);
    }

    return (
        <div className="space-y-3" data-testid="induction-issue-panel">
            <div className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                <label className="text-[12.5px] font-semibold text-[#1F1F1F]">Which induction should workers complete?</label>
                <select data-testid="induction-issue-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border text-[14px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                    {templates.length === 0 && <option value="">No induction templates</option>}
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
                </select>
                {tpl?.description && <p className="text-[12px] text-[#6F6A64] mt-2">{tpl.description}</p>}
                <p className="text-[11.5px] text-[#9A968F] mt-2">The QR you print is locked to this induction — workers scan and complete it directly, they don't choose.</p>
            </div>
            {templateId && (
                <SiteQRPoster clientId={clientId} siteId={siteId} projectId={projectId}
                    siteName={siteName} projectName={projectName}
                    templateId={templateId} templateCode={tpl?.code} templateLabel={tpl?.name || "Site Induction"} />
            )}
            {siteId && (
                <div className="bg-white border rounded-[13px] p-4 shadow-brand-sm" style={{ borderColor: "#E5E0DA" }} data-testid="project-assembly-editor">
                    <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] mb-1 flex items-center gap-1.5"><MapPin size={14} style={{ color: brand }} /> Assembly points for {siteName}</h3>
                    <p className="text-[11.5px] text-[#6F6A64] mb-3">Where everyone should gather in an emergency — shown on every induction for this site.</p>
                    <AssemblyPointsEditor siteId={siteId} points={assemblyPoints} setPoints={setAssemblyPoints} brand={brand} testidPrefix="project-assembly" />
                    <div className="flex justify-end mt-3">
                        <button data-testid="project-assembly-save" onClick={saveAssembly} disabled={assemblyBusy}
                            className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white inline-flex items-center gap-1.5" style={{ background: brand }}>
                            {assemblyBusy ? <Loader2 className="animate-spin" size={14} /> : null} Save assembly points
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function AttendancePanel({ projectId, clientId, brand }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState("");
    const load = () => {
        setLoading(true);
        const params = new URLSearchParams({ project_id: projectId });
        if (date) params.set("date", date);
        api.get(`/site-forms/attendance?${params.toString()}`)
            .then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    };
    useEffect(load, [projectId, date]);   // eslint-disable-line react-hooks/exhaustive-deps
    const onSite = rows.filter((r) => !r.time_out);
    const fmt = (s) => { try { return new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return s; } };
    return (
        <div data-testid="attendance-panel" className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                <Users className="w-4 h-4" style={{ color: brand }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] flex-1">On site now <span className="text-[#9A968F] font-normal">({onSite.length})</span></h3>
                <button data-testid="attendance-refresh" onClick={load} className="text-[#9A968F] hover:text-[#1F1F1F]"><RefreshCw size={14} /></button>
            </div>
            <div className="p-4 space-y-3">
                {onSite.length === 0 ? (
                    <p className="text-[12px] text-[#9A968F] text-center py-2">Nobody is currently signed in.</p>
                ) : (
                    <ul className="space-y-1.5" data-testid="attendance-onsite-list">
                        {onSite.map((r) => (
                            <li key={r.id} data-testid={`attendance-onsite-${r.id}`} className="flex items-center gap-2 text-[12.5px]">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                <span className="font-semibold text-[#1F1F1F]">{r.name}</span>
                                <span className="text-[#9A968F] truncate">{r.company ? `· ${r.company}` : ""}</span>
                                <span className="ml-auto text-[11px] text-[#9A968F]">in {fmt(r.time_in)}</span>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="pt-2 border-t flex items-center gap-2" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9A968F]">Log</span>
                    <input type="date" data-testid="attendance-date" value={date} onChange={(e) => setDate(e.target.value)}
                        className="h-8 px-2 rounded-md border text-[12px] ml-auto" style={{ borderColor: "#E5E0DA" }} />
                    {date && <button data-testid="attendance-date-clear" onClick={() => setDate("")} className="text-[11px] text-[#9A968F]">Clear</button>}
                </div>
                {loading ? (
                    <p className="text-[12px] text-[#9A968F] text-center py-2">Loading…</p>
                ) : rows.length === 0 ? (
                    <p className="text-[12px] text-[#9A968F] text-center py-2">No attendance recorded{date ? " on this day" : ""}.</p>
                ) : (
                    <ul className="space-y-1" data-testid="attendance-log-list">
                        {rows.map((r) => (
                            <li key={r.id} data-testid={`attendance-log-${r.id}`} className="flex items-center gap-2 text-[12px] py-1 border-b last:border-b-0" style={{ borderColor: "#F5F3F0" }}>
                                <span className="font-medium text-[#1F1F1F] truncate">{r.name}</span>
                                <span className="text-[#9A968F] truncate">{r.company ? `· ${r.company}` : ""}</span>
                                <span className="ml-auto text-[11px] text-[#6F6A64] whitespace-nowrap">
                                    {fmt(r.time_in)} → {r.time_out ? fmt(r.time_out) : <span className="text-green-600 font-semibold">on site</span>}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

const REPORT_STATUS_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "red" };
const CATEGORY_LABEL = { Inspection: "Inspection", Audit: "Audit", Fra: "Fire Risk Assessment", Bsc: "Building Safety Case", Cdm: "CDM", Induction: "Induction", Toolbox: "Toolbox Talk", Incident: "Incident" };
const categoryLabel = (c) => (c ? CATEGORY_LABEL[c] || c : "");

async function downloadReportPdf(id, templateCode) {
    const t = toast.loading("Generating PDF… this can take a moment for reports with photos.");
    try {
        const res = await api.get(`/forms/instances/${id}/pdf`, { responseType: "blob", timeout: 180000 });
        const url = window.URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = filenameFromResponse(res, `${templateCode || "report"}-${id.slice(0, 8)}.pdf`);
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success("PDF ready", { id: t });
    } catch {
        toast.error("PDF generation failed. Please try again in a moment.", { id: t });
    }
}

function ReportRows({ reports, projectId, navigate }) {
    if (!reports.length) return <div className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">No reports issued yet.</div>;
    return (
        <ul>
            {reports.map((r) => (
                <li key={r.id} data-testid={`published-report-${r.id}`}
                    onClick={() => navigate(`/app/inspections/${r.id}?back=${encodeURIComponent("/app/projects/" + projectId)}`)}
                    className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9] cursor-pointer" style={{ borderColor: "#F0EDE8" }}>
                    <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "#6F6A64" }} />
                    <div className="min-w-0 flex-1">
                        <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{r.title || r.template_name}</div>
                        <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">{r.template_name}{r.category ? ` · ${categoryLabel(r.category)}` : ""}{(r.submitted_at || r.created_at) ? ` · ${fmtDate(r.submitted_at || r.created_at)}` : ""}</div>
                    </div>
                    <Pill tone={REPORT_STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill>
                    <ArrowUpRight className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#94a0b4" }} />
                </li>
            ))}
        </ul>
    );
}

// Full filterable "Reports & Audits" list for the project — type, category,
// status and date filters over the same report/audit records (form_instances)
// used on the Home panel, no separate data source.
function ProjectReportsList({ reports, projectId, navigate, brand }) {
    const [type, setType] = useState("");
    const [category, setCategory] = useState("");
    const [status, setStatus] = useState("");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");

    const types = [...new Set(reports.map((r) => r.template_name).filter(Boolean))].sort();
    const categories = [...new Set(reports.map((r) => r.category).filter(Boolean))].sort();
    const statuses = [...new Set(reports.map((r) => r.status).filter(Boolean))];

    const filtered = reports
        .filter((r) => !type || r.template_name === type)
        .filter((r) => !category || r.category === category)
        .filter((r) => !status || r.status === status)
        .filter((r) => !from || (r.submitted_at || r.created_at || "") >= from)
        .filter((r) => !to || (r.submitted_at || r.created_at || "") <= `${to}T23:59:59`)
        .sort((a, b) => (b.submitted_at || b.created_at || "").localeCompare(a.submitted_at || a.created_at || ""));

    const selectCls = "text-[11.5px] rounded-[7px] border px-2 py-1.5 bg-white";
    const selectStyle = { borderColor: "#E5E0DA" };

    return (
        <div data-testid="project-reports-list" className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                <FileText className="w-4 h-4" style={{ color: brand }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F]">Reports & Audits</h3>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: "#F0EDE8", color: "#6F6A64" }}>{filtered.length}</span>
                <div className="flex-1" />
                <Filter className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                <select data-testid="reports-filter-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls} style={selectStyle}>
                    <option value="">All types</option>
                    {types.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select data-testid="reports-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className={selectCls} style={selectStyle}>
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                </select>
                <select data-testid="reports-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} style={selectStyle}>
                    <option value="">All statuses</option>
                    {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input data-testid="reports-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={selectCls} style={selectStyle} />
                <span className="text-[11px]" style={{ color: "#9A968F" }}>to</span>
                <input data-testid="reports-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className={selectCls} style={selectStyle} />
            </div>
            {filtered.length === 0 ? (
                <div className="px-5 py-8 text-center text-[11.5px] text-[#9A968F]">
                    {reports.length === 0 ? "No reports issued yet." : "No reports match these filters."}
                </div>
            ) : (
                <ul>
                    {filtered.map((r) => (
                        <li key={r.id} data-testid={`project-report-row-${r.id}`}
                            className="flex items-center gap-3 px-5 py-3 border-b last:border-b-0 hover:bg-[#FCFBF9]" style={{ borderColor: "#F0EDE8" }}>
                            <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "#6F6A64" }} />
                            <button data-testid={`project-report-open-${r.id}`}
                                onClick={() => navigate(`/app/inspections/${r.id}?back=${encodeURIComponent("/app/projects/" + projectId)}`)}
                                className="min-w-0 flex-1 text-left">
                                <div className="text-[12.5px] font-medium text-[#1F1F1F] truncate">{r.title || r.template_name}</div>
                                <div className="text-[10.5px] text-[#9A968F] truncate mt-0.5">
                                    {r.template_name}{r.category ? ` · ${categoryLabel(r.category)}` : ""}{(r.submitted_at || r.created_at) ? ` · ${fmtDate(r.submitted_at || r.created_at)}` : ""}
                                </div>
                            </button>
                            <Pill tone={REPORT_STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill>
                            <button data-testid={`project-report-download-${r.id}`} title="Download PDF"
                                onClick={() => downloadReportPdf(r.id, r.template_code)}
                                className="w-7 h-7 rounded-[7px] grid place-items-center hover:bg-[#F0EDE8] flex-shrink-0">
                                <Download className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

